import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Instrument, SiteSettings } from '../types';
import { calculateProductTiers } from './pricingTiers';

export interface CatalogPdfOptions {
  categoryName: string;
  instruments: Instrument[];
  settings?: SiteSettings;
  pkrRate?: number;
  cnyRate?: number;
}

/**
 * Format currency amounts cleanly
 */
function formatCurrency(amount: number, symbol: string, decimals: number = 0): string {
  if (isNaN(amount) || amount === null || amount === undefined) return `${symbol} 0`;
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${symbol} ${formatted}`;
}

/**
 * Generates and downloads a high-fidelity, multi-currency PDF catalog for a specific category or master catalog.
 */
export async function generateCategoryCatalogPdf({
  categoryName,
  instruments,
  settings,
  pkrRate = 280,
  cnyRate = 7.2,
}: CatalogPdfOptions): Promise<void> {
  const isMasterCatalog = !categoryName || categoryName === 'All' || categoryName.toLowerCase() === 'all';
  const displayCategory = isMasterCatalog ? 'All Surgical Specialties (Master Catalog)' : categoryName;
  
  // Filter instruments matching category
  const filteredItems = instruments.filter(item => {
    if (isMasterCatalog) return true;
    const cat = (item.category || '').toLowerCase();
    const target = categoryName.toLowerCase();
    return cat === target || 
      (target.includes('general') && cat.includes('general')) || 
      (target.includes('electro') && cat.includes('electro'));
  });

  if (filteredItems.length === 0) {
    throw new Error(`No products found in category "${categoryName}" to generate catalog.`);
  }

  // Create A4 Landscape PDF for professional catalog presentation
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const todayStr = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const companyName = 'HAMIM ENTERPRISES';
  const companyTagline = 'MANUFACTURER & EXPORTER OF PRECISION SURGICAL & MEDICAL INSTRUMENTS';
  const companyEmail = settings?.contactEmail || 'hamimenterprises00@gmail.com';
  // Strictly avoid fake/placeholder phone numbers. Only use if user explicitly configured one.
  const hasUserPhone = Boolean(settings?.contactPhone?.trim() || settings?.whatsappNumber?.trim());
  const companyPhone = hasUserPhone ? (settings?.contactPhone || settings?.whatsappNumber || '') : '';
  const companyAddress = settings?.address || 'Small Industrial Estate, Sialkot - 51310, Punjab, Pakistan';

  // 1. TOP HEADER BRANDING BAR
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageWidth, 75, 'F');

  // Decorative Accent Bar (indigo-600)
  doc.setFillColor(79, 70, 229);
  doc.rect(0, 75, pageWidth, 4, 'F');

  // Company Title
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(companyName, 36, 32);

  // Accent Tag
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(165, 180, 252); // indigo-200
  doc.text('ISO 13485:2016 & CE CERTIFIED SURGICAL MANUFACTURER', 36, 46);

  // Tagline & Factory Location
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(203, 213, 225); // slate-300
  doc.text(`${companyTagline} | Factory: ${companyAddress}`, 36, 59);

  // Right Side Header Contacts
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text('OFFICIAL EXPORT CATALOG', pageWidth - 36, 26, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(226, 232, 240);
  doc.text(`Email: ${companyEmail}`, pageWidth - 36, 38, { align: 'right' });
  if (companyPhone) {
    doc.text(`Phone/WhatsApp: ${companyPhone}`, pageWidth - 36, 49, { align: 'right' });
    doc.text(`Web: www.hamimenterprises.com`, pageWidth - 36, 60, { align: 'right' });
  } else {
    doc.text(`Web: www.hamimenterprises.com`, pageWidth - 36, 49, { align: 'right' });
    doc.text(`Official Export Office: Sialkot, Pakistan`, pageWidth - 36, 60, { align: 'right' });
  }

  // 2. CATEGORY BANNER SECTION
  doc.setFillColor(248, 250, 252); // slate-50
  doc.rect(36, 92, pageWidth - 72, 46, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.rect(36, 92, pageWidth - 72, 46, 'S');

  // Left banner icon/pill
  doc.setFillColor(79, 70, 229);
  doc.rect(36, 92, 6, 46, 'F');

  doc.setTextColor(30, 41, 59);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(
    `${displayCategory.toUpperCase()} - SURGICAL CATALOG`,
    52,
    111
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Total Listed Instruments: ${filteredItems.length} Products | Real-time Inventory & Export Price Schedule (USD $, PKR Rs, CNY ¥)`,
    52,
    126
  );

  // Right side of banner: Date and Rate reference
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(`Date of Issue: ${todayStr}`, pageWidth - 48, 110, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Ref Currency Baseline: 1 USD = ${pkrRate} PKR | 1 USD = ${cnyRate} CNY`, pageWidth - 48, 124, { align: 'right' });

  // 3. TABLE GENERATION USING JSPDF-AUTOTABLE
  const tableRows = filteredItems.map((item, index) => {
    const tiers = calculateProductTiers(item);
    const effectivePrice = tiers.premium.priceUsd;

    // 3 Pricing Tiers in USD
    const priceTiersStr = `Premium: $${tiers.premium.priceUsd.toFixed(2)}\nNormal: $${tiers.normal.priceUsd.toFixed(2)}\nVIP: $${tiers.vip.priceUsd.toFixed(2)}`;

    const pkrPrice = Math.round(effectivePrice * pkrRate);
    const pkrStr = `Rs. ${pkrPrice.toLocaleString()}`;

    const cnyPrice = Math.round(effectivePrice * cnyRate);
    const cnyStr = `¥ ${cnyPrice.toLocaleString()}`;

    // Item specifications & code
    const skuCode = item.code || `HE-${(item.category || 'SUR').substring(0, 3).toUpperCase()}-${String(index + 1).padStart(3, '0')}`;
    
    // Detailed Specs snippet
    const specDetails: string[] = [];
    if (item.material) specDetails.push(`Material: ${item.material}`);
    if (item.size) specDetails.push(`Size: ${item.size}`);
    if (item.length) specDetails.push(`Length: ${item.length}`);
    if (item.quality) specDetails.push(`Grade: ${item.quality}`);
    if (item.origin) specDetails.push(`Origin: ${item.origin}`);
    if (item.subcategory) specDetails.push(`Type: ${item.subcategory}`);

    const specsText = specDetails.length > 0 ? `\n[${specDetails.join(' | ')}]` : '';
    const cleanDesc = item.description 
      ? `\n${item.description.substring(0, 90)}${item.description.length > 90 ? '...' : ''}` 
      : '';

    const productFullTitle = `${item.name}${specsText}${cleanDesc}`;
    const stockStatus = (Number(item.stock) || 0) > 0 ? `In Stock (${item.stock})` : 'Made-to-Order';

    return [
      String(index + 1),
      skuCode,
      productFullTitle,
      item.subcategory || item.category || 'General',
      item.material || 'AISI 420 SS',
      stockStatus,
      priceTiersStr,
      pkrStr,
      cnyStr,
    ];
  });

  autoTable(doc, {
    startY: 148,
    margin: { left: 36, right: 36, bottom: 50, top: 85 },
    head: [[
      '#',
      'SKU / CODE',
      'INSTRUMENT SPECIFICATIONS & DESCRIPTION',
      'SUB-FIELD',
      'MATERIAL',
      'STOCK',
      '3-TIER PRICES (USD)',
      'OFFICIAL (PKR)',
      'OFFICIAL (CNY)',
    ]],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: [15, 23, 42], // slate-900
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: 6,
    },
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      cellPadding: 5,
      valign: 'middle',
      lineColor: [226, 232, 240],
      lineWidth: 0.5,
    },
    columnStyles: {
      0: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
      1: { cellWidth: 68, halign: 'center', fontStyle: 'bold', textColor: [79, 70, 229] },
      2: { cellWidth: 'auto', halign: 'left' },
      3: { cellWidth: 70, halign: 'center' },
      4: { cellWidth: 70, halign: 'center', textColor: [100, 116, 139] },
      5: { cellWidth: 64, halign: 'center' },
      6: { cellWidth: 76, halign: 'right', fontStyle: 'bold', textColor: [15, 23, 42] },
      7: { cellWidth: 80, halign: 'right', fontStyle: 'bold', textColor: [5, 150, 105] }, // emerald-600
      8: { cellWidth: 68, halign: 'right', fontStyle: 'bold', textColor: [217, 119, 6] }, // amber-600
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252], // slate-50
    },
    didDrawPage: (data) => {
      // Page Footer
      const totalPagesExp = '{total_pages_count_string}';
      const currentPage = data.pageNumber;

      // Bottom separator line
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(36, pageHeight - 34, pageWidth - 36, pageHeight - 34);

      // Left Footer text
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      const footerContact = companyPhone 
        ? `Inquiries: ${companyEmail} | Tel: ${companyPhone} | Web: www.hamimenterprises.com`
        : `Inquiries: ${companyEmail} | Web: www.hamimenterprises.com`;
      doc.text(
        `Hamim Enterprises Surgical Catalog | Department: ${displayCategory} | ${footerContact}`,
        36,
        pageHeight - 20
      );

      // Right Footer: Page number
      doc.text(
        `Page ${currentPage}`,
        pageWidth - 36,
        pageHeight - 20,
        { align: 'right' }
      );
    },
  });

  // Safe file naming
  const sanitizedCatName = displayCategory.replace(/[^a-zA-Z0-9_-]/g, '_');
  const fileName = `Hamim_Enterprises_${sanitizedCatName}_Catalog_${new Date().toISOString().split('T')[0]}.pdf`;

  // Trigger browser download
  doc.save(fileName);
}
